import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { func, number, string, bool } from 'prop-types';

import modularScale from '../../../styles/helpers/modular-scale';
import Anchor from 'components/base/Anchor/Anchor';

import passiveSupported from './passiveSupported';

import useMedia from 'hooks/useMedia';
import { StyledNoData } from '../styles';

export const Root = styled('div')`
  ${({ fixedWidth }) => (fixedWidth ? `width: ${fixedWidth}px;` : '')}
  position: ${({ isComplementary, hasComplementary }) =>
    isComplementary || hasComplementary ? 'static' : 'relative'};
  margin: ${({
    theme,
    hasComplementary,
    isComplementary,
    hasLeftAxisLable,
  }) => {
    if (hasComplementary) return `${theme.spacing.medium} 0 0`;
    if (isComplementary) return '0';
    if (hasLeftAxisLable) return `${theme.spacing.medium} 48px`;
    return `${theme.spacing.medium} 0`;
  }};
`;

export const ChartSVG = styled('svg')`
  width: 100%;
  ${({ fixedWidth }) => (fixedWidth ? `width: ${fixedWidth}px;` : '')}
  font-family: ${({ theme }) => theme.numbersTypography};
  font-size: ${modularScale(0)};
  font-weight: 400;
  color: ${({ theme }) => theme.CasperGrey};

  > g {
    transition: opacity 120ms linear;
  }

  .tick line {
    shape-rendering: crispedges;
  }
`;

export const DefaultNoDataComponent = (props) => (
  <>
    {props.hasNoData && props.showNoDataWarning && (
      <StyledNoData isSmallScreen={!props.isLargeScreen}>
        <h3>No data available</h3>
        <p>
          Unfortunately, there is no data available at this time. If there
          should be, please contact{' '}
          <Anchor to="/glossary/general-information/contact">
            Opus support
          </Anchor>
          .
        </p>
      </StyledNoData>
    )}
  </>
);

export const NoDataSelectedComponent = (props) => (
  <>
    {props.hasNoData && props.showNoDataWarning && (
      <StyledNoData isSmallScreen={!props.isLargeScreen}>
        <h3>No data selected</h3>
        <p>Please select data</p>
      </StyledNoData>
    )}
  </>
);

/**
 * Chart renders a responsive SVG element with a static height.
 *
 * @param {Object} props React props
 * @param {Boolean} isComplementary Renders the chart underneath a preceding sibling chart.
 * @param {Boolean} isDimmed Lower the opacity to 0.4
 * @param {Boolean} hasComplementary Allows this chart to render a complementary chart under it.
 * @param {Boolean} hasNoData Show a no data state if showNoDataWarning is also true
 * @param {Boolean} showNoDataWarning Should the no data state be rendered at all?
 * @param {Number} height A static height for the SVG
 *
 * @returns {Object} A functional React component
 */
export default Chart;

function Chart(props) {
  const {
    isComplementary,
    isDimmed,
    hasComplementary,
    hasNoData,
    height,
    mediaQuery,
    render: renderFn,
    showNoDataWarning,
    showTooltip,
    NoDataComponent,
    hasLeftAxisLable,
    fixedWidth,
  } = props;

  const root = useRef();
  const tooltipRoot = useRef(null);
  const isLargeScreen = useMedia(mediaQuery);

  /**
   * Initialize the width/height of the SVG.
   * Width is always taken from the root element, height may be passed as a prop,
   * or calculated based on an aspect ratio.
   */
  const [rootWidth, setWidth] = useState(0);

  useEffect(() => {
    // Set the dimensions for the first render
    setChartDimensions();

    // Listen for resize events to resize the graph when the window size changes
    window.addEventListener(
      'resize',
      setChartDimensions,
      passiveSupported ? { passive: true } : false
    );

    function setChartDimensions() {
      const sizes = root.current.getBoundingClientRect();

      // Save the width to state
      setWidth(sizes.width);
    }

    return function unmount() {
      window.removeEventListener('resize', setChartDimensions);
    };
  }, [height]);

  return (
    <Root
      fixedWidth={fixedWidth}
      ref={root}
      isComplementary={isComplementary}
      hasComplementary={hasComplementary}
      hasLeftAxisLable={hasLeftAxisLable}
    >
      {showTooltip && <div ref={tooltipRoot} />}
      <ChartSVG
        width={rootWidth}
        height={height}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <g opacity={isDimmed || hasNoData ? 0.4 : 1}>
          {renderFn({
            parentWidth: fixedWidth || rootWidth,
            parentHeight: height,
            isSmallScreen: !isLargeScreen,
            tooltipRoot,
            showTooltip,
          })}
        </g>
      </ChartSVG>
      <NoDataComponent
        showNoDataWarning={showNoDataWarning}
        hasNoData={hasNoData}
      />
    </Root>
  );
}

Chart.defaultProps = {
  mediaQuery: '(min-width: 60em)',
  isDimmed: false,
  showNoDataWarning: true,
  NoDataComponent: DefaultNoDataComponent,
};

Chart.propTypes = {
  render: func.isRequired,
  mediaQuery: string,
  height: number.isRequired,
  hasNoData: bool,
  showNoDataWarning: bool,
  isComplementary: bool,
  hasComplementary: bool,
  isDimmed: bool,
  showTooltip: bool,
  NoDataComponent: func,
  hasLeftAxisLable: bool,
  fixedWidth: number,
};
